/**
 * Página Admin - Clientes com Locação Ativa
 * 
 * Lista APENAS clientes com locação ativa, mostrando:
 * - Nome, CPF, telefone
 * - Placa + modelo da moto
 * - Status do acesso ao app PWA (sem acesso / provisória / ativa)
 * - Botão "Criar acesso + enviar WhatsApp" (mesma lógica da página Clientes)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { apiService } from '../../services/api';
import { Users, Send, Search, RefreshCw, CheckCircle2, AlertCircle, Bike, Unlock } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const cfg = {
    'Sem acesso':                  { bg: 'bg-red-500/15',     border: 'border-red-500/40',     text: 'text-red-300',     icon: AlertCircle },
    'Provisória — aguardando troca': { bg: 'bg-amber-500/15',  border: 'border-amber-500/40',   text: 'text-amber-300',   icon: AlertCircle },
    'Ativa':                       { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-300', icon: CheckCircle2 },
  }[status] || { bg: 'bg-slate-500/15', border: 'border-slate-500/40', text: 'text-slate-300', icon: AlertCircle };
  const Ico = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${cfg.bg} border ${cfg.border} ${cfg.text}`}>
      <Ico className="w-3 h-3" /> {status}
    </span>
  );
};

const ClientesAtivosPage = () => {
  const [carregando, setCarregando] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');
  const [processando, setProcessando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null);
    try {
      const { data } = await apiService.api.get('/admin/cliente-app/clientes-locacao-ativa');
      setClientes(data?.clientes || []);
    } catch (e) {
      setErro(e.response?.data?.detail || e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Cria/reseta acesso + abre WhatsApp (mesma lógica da página Clientes)
  const criarAcesso = async (c) => {
    const isReset = c.app_tem_acesso;
    const acao = isReset ? 'Resetar a senha do app' : 'Criar acesso ao app';

    // 1. Confirma a operação
    if (!window.confirm(`${acao} para ${c.cliente_nome}?`)) return;

    // 2. Pergunta se quer senha customizada
    const senha_customizada = window.prompt(
      `Digite uma senha personalizada (mín. 4 caracteres) OU deixe vazio pra gerar aleatória:`,
      ''
    );
    // Se cancelou o prompt, aborta
    if (senha_customizada === null) return;

    setProcessando(c.cliente_id);
    try {
      const endpoint = isReset
        ? `/admin/clientes/${c.cliente_id}/resetar-senha-app`
        : `/admin/clientes/${c.cliente_id}/criar-acesso-app`;
      const body = senha_customizada.trim().length >= 4
        ? { senha_customizada: senha_customizada.trim() }
        : {};
      const { data } = await apiService.api.post(endpoint, body);
      const senha = data.senha_inicial;
      const placa = data.placa || c.placa || '<placa da moto>';
      const url = `${window.location.origin}/cliente`;
      const primeiroNome = (c.cliente_nome || '').split(' ')[0] || 'Cliente';
      const acaoMsg = isReset ? 'sua senha foi *resetada*' : 'seu acesso ao app *Desloca* foi criado';

      const mensagem =
        `🏍️ *Olá, ${primeiroNome}!*\n\n` +
        `${acaoMsg}. Acompanhe pagamentos, débitos, comprovantes e o status da sua moto direto pelo celular.\n\n` +
        `🔗 ${url}\n\n` +
        `🔐 *Seus dados de acesso:*\n` +
        `👤 CPF: ${c.cliente_cpf}\n` +
        `🏍️ Placa: ${placa}\n` +
        `🔑 Senha provisória: ${senha}\n\n` +
        `⚠️ No primeiro acesso, você criará sua senha pessoal.\n\n` +
        `📱 *Como instalar no celular:*\n` +
        `• iPhone (Safari): toque em "Compartilhar" → "Adicionar à Tela de Início"\n` +
        `• Android (Chrome): toque no menu (3 pontinhos) → "Instalar app"`;

      const telLimpo = String(c.cliente_telefone || '').replace(/\D/g, '');
      const telE164 = telLimpo.startsWith('55') ? telLimpo : `55${telLimpo}`;

      // SEMPRE copia a mensagem pra clipboard ANTES de abrir WhatsApp (fallback se Safari bloquear)
      try { await navigator.clipboard.writeText(mensagem); } catch {/* sem permissão de clipboard */}

      if (telLimpo.length >= 10) {
        // iOS Safari bloqueia window.open após await — uso location.href (mesma aba) que funciona sempre
        const waUrl = `https://wa.me/${telE164}?text=${encodeURIComponent(mensagem)}`;
        alert(`✅ ${isReset ? 'Senha resetada' : 'Acesso criado'}!\n\nSenha: ${senha}\nA mensagem foi copiada para a área de transferência.\n\nVou abrir o WhatsApp agora — se não abrir automaticamente, cole a mensagem manualmente.`);
        window.location.href = waUrl;
      } else {
        alert(`✅ ${isReset ? 'Senha resetada' : 'Acesso criado'}!\n\n⚠️ Sem telefone cadastrado.\n\nSenha: ${senha}\n\nA mensagem completa foi copiada pra área de transferência — basta colar no WhatsApp manualmente.`);
      }
      carregar();
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessando(null);
    }
  };

  const desbloquearTentativas = async (c) => {
    const cpf = String(c.cliente_cpf || '').replace(/\D/g, '');
    if (!cpf) return;
    if (!window.confirm(`Desbloquear tentativas de login de ${c.cliente_nome}?\n\nO cliente vai poder tentar logar imediatamente sem esperar 1 minuto.`)) return;
    setProcessando(c.cliente_id);
    try {
      const { data } = await apiService.api.post(
        '/admin/cliente-app/desbloquear-tentativas',
        { identificador: cpf }
      );
      if (data?.limpadas > 0) {
        alert(`✅ Desbloqueado!\n\n${data.limpadas} registro(s) de tentativas removido(s) — cliente pode logar agora.`);
      } else {
        alert(`ℹ️ Nenhum bloqueio encontrado para ${c.cliente_nome} — o cliente já pode tentar logar normalmente.`);
      }
    } catch (e) {
      alert('Erro: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessando(null);
    }
  };

  const filtrados = clientes.filter(c => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return [c.cliente_nome, c.cliente_cpf, c.placa, c.cliente_telefone]
      .some(v => String(v || '').toLowerCase().includes(q));
  });

  // KPIs
  const semAcesso  = clientes.filter(c => !c.app_tem_acesso).length;
  const provisoria = clientes.filter(c => c.app_tem_acesso && c.app_senha_provisoria).length;
  const ativos     = clientes.filter(c => c.app_tem_acesso && !c.app_senha_provisoria).length;

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl mx-auto" data-testid="clientes-ativos-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 sm:w-7 sm:h-7 text-emerald-400 shrink-0" />
            <span className="truncate">Clientes com Locação Ativa</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1 hidden sm:block">
            Apenas inquilinos com moto alugada agora. Use o botão pra gerar o acesso ao app via WhatsApp.
          </p>
        </div>
        <button
          onClick={carregar}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition shrink-0"
          data-testid="clientes-ativos-recarregar"
          aria-label="Recarregar"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Recarregar</span>
        </button>
      </div>

      {/* KPIs — 2x2 no mobile, 4 colunas no desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <KPI label="Total ativos" value={clientes.length} color="text-white" testid="kpi-total" />
        <KPI label="Sem acesso ao app" value={semAcesso} color="text-red-400" testid="kpi-sem-acesso" />
        <KPI label="Senha provisória" value={provisoria} color="text-amber-400" testid="kpi-provisoria" />
        <KPI label="Acesso ativo" value={ativos} color="text-emerald-400" testid="kpi-ativos" />
      </div>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome, CPF, placa ou telefone..."
          className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
          data-testid="clientes-ativos-busca"
        />
      </div>

      {erro && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-lg p-3 text-sm" data-testid="clientes-ativos-erro">
          {erro}
        </div>
      )}

      {carregando ? (
        <div className="p-8 text-center text-slate-400">
          <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" /> Carregando...
        </div>
      ) : filtrados.length === 0 ? (
        <div className="p-8 text-center text-slate-400 bg-slate-800/40 rounded-xl border border-slate-700">
          Nenhum cliente com locação ativa encontrado.
        </div>
      ) : (
        <>
          {/* MOBILE: cards verticais (< lg) */}
          <div className="lg:hidden space-y-3">
            {filtrados.map(c => (
              <div
                key={c.cliente_id}
                className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3"
                data-testid={`card-cliente-${c.cliente_id.slice(0,8)}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold truncate">{c.cliente_nome || '-'}</div>
                    <div className="text-xs text-slate-400 font-mono mt-0.5">{c.cliente_cpf || '-'}</div>
                  </div>
                  <StatusBadge status={c.app_status_label} />
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-slate-300">
                    <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-0.5">Telefone</div>
                    {c.cliente_telefone || '-'}
                  </div>
                  <div className="text-slate-300">
                    <div className="text-[10px] uppercase text-slate-500 tracking-wider mb-0.5 flex items-center gap-1">
                      <Bike className="w-3 h-3 text-emerald-400" /> Moto
                    </div>
                    <span className="font-mono">{c.placa || '-'}</span>
                    {c.moto_modelo && <span className="block text-[11px] text-slate-400 truncate">{c.moto_modelo}</span>}
                  </div>
                </div>

                <div className="flex gap-2 pt-1 border-t border-slate-700/60">
                  <button
                    onClick={() => desbloquearTentativas(c)}
                    disabled={processando === c.cliente_id}
                    title="Desbloquear tentativas de login"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-slate-200 rounded-lg text-xs flex-shrink-0"
                    data-testid={`btn-desbloquear-mobile-${c.cliente_id.slice(0,8)}`}
                  >
                    <Unlock className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => criarAcesso(c)}
                    disabled={processando === c.cliente_id}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-900 rounded-lg text-sm font-semibold"
                    data-testid={`btn-acesso-mobile-${c.cliente_id.slice(0,8)}`}
                  >
                    <Send className="w-4 h-4" />
                    {processando === c.cliente_id
                      ? '...'
                      : c.app_tem_acesso ? 'Resetar + WhatsApp' : 'Criar + WhatsApp'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* DESKTOP: tabela tradicional (≥ lg) */}
          <div className="hidden lg:block bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/60 text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-left">CPF</th>
                  <th className="px-3 py-2 text-left">Telefone</th>
                  <th className="px-3 py-2 text-left">Moto</th>
                  <th className="px-3 py-2 text-left">Status app</th>
                  <th className="px-3 py-2 text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/60">
                {filtrados.map(c => (
                  <tr key={c.cliente_id} className="hover:bg-slate-700/30" data-testid={`linha-cliente-${c.cliente_id.slice(0,8)}`}>
                    <td className="px-3 py-2 text-white">{c.cliente_nome || '-'}</td>
                    <td className="px-3 py-2 text-slate-300 font-mono text-xs">{c.cliente_cpf || '-'}</td>
                    <td className="px-3 py-2 text-slate-300">{c.cliente_telefone || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 text-slate-300">
                        <Bike className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="font-mono text-xs">{c.placa || '-'}</span>
                        {c.moto_modelo && <span className="text-xs text-slate-400">— {c.moto_modelo}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2"><StatusBadge status={c.app_status_label} /></td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => desbloquearTentativas(c)}
                          disabled={processando === c.cliente_id}
                          title="Desbloquear tentativas de login (caso o cliente esteja preso pelo limite de 50 tentativas)"
                          className="inline-flex items-center gap-1 px-2 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-slate-200 rounded-lg text-xs transition"
                          data-testid={`btn-desbloquear-${c.cliente_id.slice(0,8)}`}
                        >
                          <Unlock className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => criarAcesso(c)}
                          disabled={processando === c.cliente_id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-900 rounded-lg text-xs font-semibold transition"
                          data-testid={`btn-acesso-${c.cliente_id.slice(0,8)}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                          {processando === c.cliente_id ? '...' :
                           c.app_tem_acesso ? 'Resetar + WhatsApp' : 'Criar + WhatsApp'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

const KPI = ({ label, value, color, testid }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl p-3 sm:p-4">
    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider leading-tight">{label}</p>
    <p className={`text-2xl sm:text-3xl font-bold mt-1 ${color}`} data-testid={testid}>{value}</p>
  </div>
);

export default ClientesAtivosPage;
